/**
 * SidebarStep5_FutureClosure - Forced-Closure Evidence (Future Closure Path)
 *
 * Per the 2026-07-08 architecture decision, the fuzzy float inputs are pruned. The old
 * manual expense-continuation percentages and granular shutdown/restart cost fields fed
 * the legacy float engine and are RETIRED: continuation is now derived deterministically
 * from the method-locked BI base, and one-time costs are captured as the phased assembler's
 * evidence-gated components below.
 *
 * This step collects only the forced-closure evidence values a human reads off documents
 * (property replacement, marketing/ICW, permanent-closure diminished business value) and
 * shows the DATA-DERIVED recovery period. Everything else the deterministic engine derives.
 *
 * Shows when calculation_mode === 'future_closure'.
 */

import React, { useState, useEffect } from 'react';
import {
  Info,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Building,
  Loader2,
  Megaphone,
  TrendingDown,
  Clock
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import { getPhasedPreview, PhasedPreviewResult } from '@/services/futureClosureApi';

interface SidebarStep5FutureClosureProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  goToStep?: (step: number) => void;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
}

export default function SidebarStep5_FutureClosure({
  onNext,
  onBack,
  data,
  onDataUpdate,
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  highestCompletedStep,
  backendCompletionPercent,
  claimId
}: SidebarStep5FutureClosureProps) {
  // Phased damage components — evidence-gated inputs consumed by the deterministic
  // phased assembler (property replacement, marketing/ICW, permanent-closure DBV).
  const [fcComponents, setFcComponents] = useState({
    property_replacement_cost: data.fc_property_replacement?.cost || 0,
    marketing_icw_cost: data.fc_marketing_icw?.cost || 0,
    permanent_closure: data.fc_dbv?.permanent_closure || false,
    value_before: data.fc_dbv?.value_before || 0,
    value_at_closure: data.fc_dbv?.value_at_closure || 0
  });

  const handleFcChange = (field: keyof typeof fcComponents, value: number | boolean) => {
    setFcComponents(prev => ({ ...prev, [field]: value }));
  };

  // Recovery period is DATA-DERIVED by default. An attorney may override, but
  // an override is FLAGGED as not-data-supported (rampOverride).
  const [recovery, setRecovery] = useState<PhasedPreviewResult | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [rampOverride, setRampOverride] = useState<{ enabled: boolean; months: number }>({
    enabled: data.fc_ramp?.override_enabled || false,
    months: data.fc_ramp?.override_months || 0
  });

  // Derive closure timing for the preview call: closure starts at the event
  // start month (YYYY-MM from event_start_date) and runs for the Step-2 duration.
  const eventStartDate: string | undefined = data.event_start_date;
  const closureStartMonth = eventStartDate ? eventStartDate.slice(0, 7) : undefined;
  const closureDurationMonths =
    data.closure_duration_months || data.future_closure_params?.closure_duration_months;
  const closureDuration = closureDurationMonths || 36;

  useEffect(() => {
    let cancelled = false;
    const fetchRecovery = async () => {
      if (!claimId || !closureStartMonth || !closureDurationMonths) {
        return;
      }
      setRecoveryLoading(true);
      setRecoveryError(null);
      try {
        const result = await getPhasedPreview(claimId, {
          closure_start_month: closureStartMonth,
          closure_duration_months: closureDurationMonths
        });
        if (!cancelled) setRecovery(result);
      } catch (err: any) {
        if (!cancelled) {
          setRecoveryError(
            err?.response?.data?.detail ||
              'Could not derive the recovery period yet. Ensure financials are uploaded.'
          );
        }
      } finally {
        if (!cancelled) setRecoveryLoading(false);
      }
    };
    fetchRecovery();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, closureStartMonth, closureDurationMonths]);

  // Handle next — persists ONLY the deterministic phased evidence (fc_* keys).
  const handleNext = () => {
    onNext({
      fc_property_replacement: { cost: fcComponents.property_replacement_cost },
      fc_marketing_icw: { cost: fcComponents.marketing_icw_cost },
      fc_dbv: {
        permanent_closure: fcComponents.permanent_closure,
        value_before: fcComponents.value_before,
        value_at_closure: fcComponents.value_at_closure,
      },
      fc_ramp: {
        recovery_period_source: recovery?.recovery_period_source ?? null,
        recovery_period_derived: recovery?.recovery_period_derived ?? null,
        recovery_period_used: rampOverride.enabled
          ? rampOverride.months
          : recovery?.recovery_period_used ?? null,
        data_supported: recovery?.data_supported ?? null,
        override_enabled: rampOverride.enabled,
        override_months: rampOverride.months,
      },
    });
  };

  const hasEvidence =
    fcComponents.property_replacement_cost > 0 ||
    fcComponents.marketing_icw_cost > 0 ||
    fcComponents.permanent_closure;
  const grade = hasEvidence || recovery?.recovery_period_source === 'data_derived' ? 'B' : 'C';

  const buildSections = () => [
    {
      id: 'recovery',
      label: 'Recovery Period',
      isActive: true,
      isComplete: !!recovery,
      onClick: () => document.getElementById('phased-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'components',
      label: 'Damage Components',
      isActive: false,
      isComplete: hasEvidence,
      onClick: () => document.getElementById('phased-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];
    if (recovery?.recovery_period_source === 'data_derived') {
      items.push({ id: 'recovery', label: 'Recovery period data-derived', status: 'complete' });
    } else if (recovery?.recovery_period_source === 'projection_unsupported') {
      items.push({ id: 'recovery', label: 'Recovery period unsupported', status: 'warning' });
    }
    if (fcComponents.property_replacement_cost > 0) items.push({ id: 'property', label: 'Property replacement set', status: 'complete' });
    if (fcComponents.marketing_icw_cost > 0) items.push({ id: 'marketing', label: 'Marketing/ICW set', status: 'complete' });
    if (fcComponents.permanent_closure) items.push({ id: 'dbv', label: 'Permanent closure (DBV)', status: 'complete' });
    return items;
  };

  return (
    <WizardV2Layout
      currentStep={5}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Forced-Closure Evidence"
      stepSubtitle={`Evidence-gated damage components for the ${closureDuration}-month closure`}
      completionPercent={backendCompletionPercent ?? 80}
      grade={grade}
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNext}
      claimId={claimId}
      nextLabel="Continue to Data Review"
      isNextDisabled={false}
      showNext={true}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {/* What's automated (sets expectation: fuzzy inputs are gone) */}
        <div className="rounded-xl p-5" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
            <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
              Growth, expense continuation, and the recovery period are derived deterministically from the
              business's own history and post-reopening data — no manual assumptions. Enter only the
              forced-closure evidence values documented in the case below.
            </p>
          </div>
        </div>

        {/* Forced-Closure Damage Components (phased assembler) */}
        <div id="phased-section" className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            </div>
            <div>
              <h3 className="cs-h3">Forced-Closure Damage Components</h3>
              <p className="cs-meta">Phased, PV-discounted damages — the accurate model that replaces the legacy projection</p>
            </div>
          </div>
          <p className="cs-meta mb-6" style={{ color: 'var(--color-text-muted)' }}>
            The recovery period is derived from your business's own post-reopening data.
            Property, marketing, and business-value inputs are evidence-gated additive components.
          </p>

          {/* Recovery-period lost profits (data-derived) */}
          <div className="mb-6 p-5 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              <span className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>Recovery-Period Lost Profits</span>
            </div>

            {recoveryLoading && (
              <div className="flex items-center gap-2 cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                <Loader2 className="w-4 h-4 animate-spin" /> Deriving recovery period from post-reopening data…
              </div>
            )}

            {!recoveryLoading && recoveryError && (
              <div className="flex items-start gap-2 cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-warning, #b45309)' }} />
                <span>{recoveryError}</span>
              </div>
            )}

            {!recoveryLoading && !recoveryError && !recovery && (
              <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
                Recovery period will be derived once closure timing (Step 2) and financials are in place.
              </p>
            )}

            {!recoveryLoading && recovery && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="cs-meta">Derived recovery period</span>
                  <span className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-text-primary)' }}>
                    {recovery.recovery_period_used != null ? `${recovery.recovery_period_used} months` : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {recovery.recovery_period_source === 'data_derived' && (
                    <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      <CheckCircle className="w-3.5 h-3.5" /> Data-derived
                      {recovery.goodness_of_fit != null && ` (R² ${recovery.goodness_of_fit.toFixed(2)})`}
                    </span>
                  )}
                  {recovery.recovery_period_source === 'projection_unsupported' && (
                    <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                      <AlertTriangle className="w-3.5 h-3.5" /> Insufficient post-reopening data — not projected
                    </span>
                  )}
                  {recovery.recovery_period_source === 'attorney_override' && (
                    <span className="cs-meta inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--color-bg-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                      <Info className="w-3.5 h-3.5" /> Attorney override
                    </span>
                  )}
                </div>

                {/* Attorney override (flagged as not-data-supported) */}
                <div className="pt-3 mt-1" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rampOverride.enabled}
                      onChange={(e) => setRampOverride(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    <span className="cs-meta">Override recovery period (attorney judgment)</span>
                  </label>
                  {rampOverride.enabled && (
                    <div className="mt-3">
                      <div className="flex items-center gap-3">
                        <div className="relative w-40">
                          <input
                            type="number"
                            value={rampOverride.months || ''}
                            onChange={(e) => setRampOverride(prev => ({ ...prev, months: parseInt(e.target.value) || 0 }))}
                            placeholder="Months"
                            className="cs-figure w-full pl-4 pr-16 py-3 rounded-lg focus:ring-2"
                            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                          />
                          <span className="cs-meta absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>months</span>
                        </div>
                      </div>
                      <p className="cs-meta mt-2 flex items-start gap-1.5" style={{ color: 'var(--color-text-muted)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-warning, #b45309)' }} />
                        Overriding the data-derived period is flagged as not-data-supported; the numbers may not back it up.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Property replacement + marketing/ICW */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="cs-label flex items-center gap-1.5 mb-2">
                <Building className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                Property Replacement / Repair
              </label>
              <div className="relative">
                <span className="cs-figure absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={fcComponents.property_replacement_cost || ''}
                  onChange={(e) => handleFcChange('property_replacement_cost', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="cs-figure w-full pl-8 pr-4 py-3 rounded-lg focus:ring-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                />
              </div>
              <p className="cs-meta mt-1.5" style={{ color: 'var(--color-text-muted)' }}>Documented cost to replace/repair damaged property.</p>
            </div>
            <div>
              <label className="cs-label flex items-center gap-1.5 mb-2">
                <Megaphone className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                Marketing / ICW Mitigation
              </label>
              <div className="relative">
                <span className="cs-figure absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={fcComponents.marketing_icw_cost || ''}
                  onChange={(e) => handleFcChange('marketing_icw_cost', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="cs-figure w-full pl-8 pr-4 py-3 rounded-lg focus:ring-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                />
              </div>
              <p className="cs-meta mt-1.5" style={{ color: 'var(--color-text-muted)' }}>Extra spend to mitigate loss / relaunch (increased cost of working).</p>
            </div>
          </div>

          {/* Diminished Business Value (permanent closure) */}
          <div className="mt-4 p-5 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={fcComponents.permanent_closure}
                onChange={(e) => handleFcChange('permanent_closure', e.target.checked)}
              />
              <TrendingDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              <span className="cs-body font-medium" style={{ color: 'var(--color-text-primary)' }}>Business permanently closed (diminished business value)</span>
            </label>
            {fcComponents.permanent_closure && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="cs-label block mb-2">Going-Concern Value Before Event</label>
                    <div className="relative">
                      <span className="cs-figure absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                      <input
                        type="number"
                        value={fcComponents.value_before || ''}
                        onChange={(e) => handleFcChange('value_before', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="cs-figure w-full pl-8 pr-4 py-3 rounded-lg focus:ring-2"
                        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="cs-label block mb-2">Residual / Scrap Value at Closure</label>
                    <div className="relative">
                      <span className="cs-figure absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                      <input
                        type="number"
                        value={fcComponents.value_at_closure || ''}
                        onChange={(e) => handleFcChange('value_at_closure', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="cs-figure w-full pl-8 pr-4 py-3 rounded-lg focus:ring-2"
                        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                      />
                    </div>
                  </div>
                </div>
                <p className="cs-meta mt-3 flex items-start gap-1.5" style={{ color: 'var(--color-text-muted)' }}>
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  When the business permanently closes, damages shift from recovery-period lost profits to the lost going-concern value. Recovery-period inputs above do not apply.
                </p>
              </>
            )}
          </div>
        </div>

      </div>
    </WizardV2Layout>
  );
}
